// Phase HR-002 hardening — sync-queue status indicator.
//
// Sits in the header. Shows nothing when the queue is empty (the common
// case — most saves succeed first try). When the backend hiccups and the
// retry layer hands a save to the offline queue, this lights up so the
// operator knows their data is safe and pending sync.
//
// Tap/click → drawer listing each pending or failed item, with retry and
// drop actions. We never silently delete operator data.

import { useEffect, useState } from 'react'
import { Badge, Button, Drawer, Empty, List, Space, Tag, Tooltip, Typography } from 'antd'
import { CloudSyncOutlined, ReloadOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons'
import {
  onQueueChanged, listQueue, countQueue, replayOnce, retryItem, deleteItem,
  type QueueItem,
} from '@/shared/api/offlineQueue'

const { Text } = Typography

interface SyncIndicatorProps {
  isMobile?: boolean
}

export default function SyncIndicator({ isMobile }: SyncIndicatorProps) {
  const [pending, setPending] = useState(0)
  const [failed, setFailed] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [items, setItems] = useState<QueueItem[]>([])
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  // Refresh counts when the queue changes (any enqueue / dequeue / status).
  useEffect(() => {
    const refresh = async () => {
      const c = await countQueue()
      setPending(c.pending)
      setFailed(c.failed)
      if (drawerOpen) setItems(await listQueue())
    }
    void refresh()
    return onQueueChanged(() => { void refresh() })
  }, [drawerOpen])

  // Online/offline indicator
  useEffect(() => {
    const onOnline = () => setOnline(true)
    const onOffline = () => setOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  const total = pending + failed
  // Hide entirely when nothing is queued AND we're online — header stays
  // clean during normal operation.
  if (total === 0 && online) return null

  // Failed items are LOUDER than pending — they need user attention.
  // Red badge for offline OR failed items; blue badge when only syncing.
  const color =
    !online ? '#cf1322' :          // offline → red
    failed > 0 ? '#cf1322' :        // permanently-failed → red (needs action)
    pending > 0 ? '#1677ff' :       // syncing → blue
    '#52c41a'                       // all clear → green (rare path)

  const label =
    !online ? 'Offline — data queued locally' :
    failed > 0
      ? `${failed} need attention${pending ? ` · ${pending} syncing` : ''}`
      : `${pending} syncing`

  return (
    <>
      <Tooltip title={label}>
        <Button
          type="text"
          onClick={() => setDrawerOpen(true)}
          icon={
            <Badge count={total} size="small" offset={[2, 0]} color={color}>
              <CloudSyncOutlined
                style={{ fontSize: isMobile ? 20 : 18, color: isMobile ? '#fff' : color }}
              />
            </Badge>
          }
          style={{
            width: isMobile ? 44 : 36,
            height: isMobile ? 44 : 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 10,
          }}
        />
      </Tooltip>

      <Drawer
        title={
          <Space>
            <CloudSyncOutlined />
            <span>Pending sync</span>
            {!online && <Tag color="red">Offline</Tag>}
          </Space>
        }
        placement="right"
        width={Math.min(440, window.innerWidth - 24)}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { void replayOnce() }}
            disabled={!online}
          >
            Sync now
          </Button>
        }
      >
        {items.length === 0 ? (
          <Empty description="Nothing queued — all data is synced." />
        ) : (
          <>
            {failed > 0 ? (
              <Text style={{ display: 'block', marginBottom: 12, color: '#cf1322' }}>
                <WarningOutlined /> Some saves failed to sync after multiple retries.
                The request body may reference data that's no longer valid
                (e.g. a machine that was deleted, or a side already taken).
                Review each one and either <b>Retry</b> (if you fixed the underlying issue)
                or <b>Discard</b> (🗑️ icon).
              </Text>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                These saves were queued because the backend was briefly busy.
                They'll sync automatically. You can keep working — your data is
                safe on this device.
              </Text>
            )}
            <List
              dataSource={items}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="retry" size="small" icon={<ReloadOutlined />}
                      onClick={() => { void retryItem(item.id) }}
                      disabled={item.status === 'REPLAYING'}
                    >Retry</Button>,
                    <Button
                      key="del" size="small" danger icon={<DeleteOutlined />}
                      onClick={() => { void deleteItem(item.id) }}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={4} wrap>
                        <Tag color={
                          item.status === 'FAILED' ? 'red'
                          : item.status === 'REPLAYING' ? 'processing'
                          : 'blue'
                        }>{item.status}</Tag>
                        <Tag>{item.method}</Tag>
                        <Text code style={{ fontSize: 11 }}>{item.url}</Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Queued {new Date(item.enqueued_at).toLocaleString()}
                          {item.attempts > 0 && ` · ${item.attempts} retry${item.attempts === 1 ? '' : 'ies'}`}
                        </Text>
                        {item.last_error && (
                          <Text type="danger" style={{ fontSize: 11 }}>
                            <WarningOutlined /> {item.last_error}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Drawer>
    </>
  )
}
